// Admin API functions

import type { SystemHealth, AdminMetrics, AdminUser } from './admin.schema';
import { apiFetch } from '../../shared/api/apiClient'; // attach API key for protected routes

// task-36: canonical display names for each payment provider key.
// Exported so all admin views (AdminPageView, AdminUsersPageView, AdminPaymentEventsPageView)
// share the same label map — adding a new provider only requires updating this object once.
export const PROVIDER_DISPLAY_NAMES: Readonly<Record<string, string>> = {
  braintree: 'Braintree',
  creem: 'Creem',
  lemonsqueezy: 'Lemon Squeezy',
};

// task-36: fetch the canonical supported-provider list from the backend.
// Calls GET /api/admin/providers which reads SUPPORTED_PAYMENT_PROVIDERS in localVars.ts.
// Returns a plain string[] so callers are not coupled to backend tuple types.
export async function fetchSupportedProviders(): Promise<string[]> {
  const response = await apiFetch('/api/admin/providers'); // protected endpoint added in registerFeatures for task-36
  if (!response.ok) throw new Error('Failed to fetch supported providers');
  const json = await response.json();
  return Array.isArray(json?.providers) ? json.providers : []; // guard against malformed response
}

// Fetch system health status
export async function fetchSystemHealth(): Promise<SystemHealth> {
  // corrected endpoint: health check routes are mounted at `/healthChk/`
  // rationale: aligns with routes/api/healthChk.ts via configureRoutes
  const response = await apiFetch('/healthChk/'); // base health check endpoint (public, no API key needed)
  if (!response.ok) {
    throw new Error('Failed to fetch system health');
  }
  const json = await response.json();
  return {
    status: json?.status === 'ok' ? 'healthy' : 'error',
    uptime: 0,
    memory: { used: 0, total: 1 },
    database: { connected: json?.status === 'ok', latency: 0 },
    services: [],
  };
}

// Fetch admin metrics
export async function fetchAdminMetrics(): Promise<AdminMetrics> {
  // corrected endpoint: backend provides admin metrics via `/api/analytics/kpis`, protected by API key middleware
  // attach API key via apiFetch; if no key available, request is forwarded unchanged (may 401)
  try {
    const response = await apiFetch('/api/analytics/kpis'); // use wrapper to send Authorization/x-api-key headers when configured
    if (!response.ok) {
      throw new Error('Failed to fetch admin metrics');
    }
    const json = await response.json();
    const kpis = json?.data?.kpis || json?.result?.kpis || {};
    return {
      totalTransactions: kpis?.payments?.total30Days ?? 0,
      totalUsers: kpis?.subscriptions?.total ?? 0,
      revenue: kpis?.payments?.revenue30Days ?? 0,
      activeSubscriptions: kpis?.subscriptions?.active ?? 0,
    };
  } catch (error) {
    throw new Error('Failed to fetch admin metrics: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
}

// task-30: Fetch the active (default) payment provider and the full list of supported providers.
// Operators use this to see which provider is live without reading config files directly.
// task-45: Added `source` to distinguish whether the active provider was set via the admin UI
// (persisted to DB, source = 'db') or is just the env-var default (source = 'env').
export interface ActiveProviderInfo {
  defaultProvider: string;
  supportedProviders: string[];
  source: 'db' | 'env'; // task-45: 'db' = admin persisted a switch; 'env' = env-var default is active
}

export async function fetchActiveProvider(): Promise<ActiveProviderInfo> {
  const response = await apiFetch('/api/admin/provider'); // backend endpoint added in registerFeatures for task-30
  if (!response.ok) {
    throw new Error('Failed to fetch active provider');
  }
  const json = await response.json();
  return {
    defaultProvider: typeof json?.defaultProvider === 'string' ? json.defaultProvider : 'unknown',
    supportedProviders: Array.isArray(json?.supportedProviders) ? json.supportedProviders : [],
    source: json?.source === 'db' ? 'db' : 'env' // task-45: default to 'env' if field absent (older backend)
  };
}

// task-30: switch the global active payment provider from the admin dashboard.
// Calls PATCH /api/admin/provider; on success the backend applies the override in-process.
// The change is runtime-scoped and resets on server restart — permanent changes need env-var update.
export async function switchActiveProvider(provider: string): Promise<ActiveProviderInfo> {
  if (!provider) throw new Error('provider is required');
  const response = await apiFetch('/api/admin/provider', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider })
  });
  if (!response.ok) {
    let msg = 'Failed to switch provider';
    try { const b = await response.json(); if (b?.error) msg = b.error; } catch {}
    throw new Error(msg);
  }
  const json = await response.json();
  return {
    defaultProvider: typeof json?.defaultProvider === 'string' ? json.defaultProvider : provider,
    supportedProviders: Array.isArray(json?.supportedProviders) ? json.supportedProviders : [],
    source: json?.source === 'db' ? 'db' : 'env' // task-45: PATCH response now includes source
  };
}

// task-45: clear the persisted provider override so the server reverts to the env-var default.
// Calls DELETE /api/admin/provider; on success the badge source changes to 'env'.
export async function clearActiveProvider(): Promise<ActiveProviderInfo> {
  const response = await apiFetch('/api/admin/provider', { method: 'DELETE' });
  if (!response.ok) {
    let msg = 'Failed to clear provider override';
    try { const b = await response.json(); if (b?.error) msg = b.error; } catch {}
    throw new Error(msg);
  }
  const json = await response.json();
  return {
    defaultProvider: typeof json?.defaultProvider === 'string' ? json.defaultProvider : 'unknown',
    supportedProviders: Array.isArray(json?.supportedProviders) ? json.supportedProviders : [],
    source: 'env' // DELETE always reverts to env-var, backend confirms same
  };
}

// task-31: per-plan credit configuration for Lemon Squeezy subscriptions
export interface LsPlanCreditsConfig {
  default: number;
  plans: {
    basic?: number;
    premium?: number;
    enterprise?: number;
  };
}

export async function fetchLsPlanCredits(): Promise<LsPlanCreditsConfig> {
  const response = await apiFetch('/api/admin/ls-plan-credits');
  if (!response.ok) throw new Error('Failed to fetch LS plan credits config');
  return response.json();
}

export async function updateLsPlanCredits(updates: Partial<{ default: number; plans: Partial<Record<'basic' | 'premium' | 'enterprise', number>> }>): Promise<LsPlanCreditsConfig> {
  const response = await apiFetch('/api/admin/ls-plan-credits', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(updates)
  });
  if (!response.ok) {
    let msg = 'Failed to update LS plan credits';
    try { const b = await response.json(); if (b?.error) msg = b.error; } catch {}
    throw new Error(msg);
  }
  const json = await response.json();
  return json.config ?? json;
}

// Fetch admin users
export async function fetchAdminUsers(): Promise<AdminUser[]> {
  // corrected endpoint: users list is exposed by @bijikyu/qryptauth-backed router at `/api/users`
  // rationale: align with newly mounted usersRoutes integrating `@bijikyu/qryptauth` library
  try {
    const response = await apiFetch('/api/users'); // updated to users listing with API key
    if (response.status < 200 || response.status >= 300) {
      throw new Error('Failed to fetch admin users');
    }
    return response.data;
  } catch (error) {
    throw new Error('Failed to fetch admin users: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
}

// Update user role
export async function updateUserRole(userId: string, role: string): Promise<{ success: boolean }> {
  // corrected endpoint: role updates are available at `/api/users/:id/role`
  // rationale: provided by usersRoutes backed by Mongo and `@bijikyu/qryptauth`
  try {
    const response = await apiFetch(`/api/users/${userId}/role`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role }),
    });
    
    if (response.status < 200 || response.status >= 300) {
      throw new Error('Failed to update user role');
    }
    return response.data;
  } catch (error) {
    throw new Error('Failed to update user role: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
}
